import { useMemo, useState } from 'react'
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import { Activity, CircleCheck, ClipboardCheck, ClipboardList, Clock, MessageSquare, PhoneOff, Siren, UserX, type LucideIcon, CalendarCheck, BedDouble, MessageSquareText, Send, LayoutList } from 'lucide-react'
import { isNotFoundError, Skeleton } from '../../core/index.js'
import { ENGINE_FILTER, type FacilityActivityBehavior, type FacilityActivityEntry } from '../types.js'
import { NotificationIcon } from '../components/notificationIcons.js'
import { EndpointHint } from '../../core/index.js'
import type { FacilityActivityApi } from './facilityActivityApi.js'
import { MESSAGES_FILTER, describeEntry, earlierSince, formatWhen, windowLabel, type ActivityTone } from './activityText.js'
import { LoadFailure } from '../../core/index.js'

/** The chip that asks for everything: a choice of its own, so nothing reads as selected before a person picks. */
const ALL_FILTER = 'all'

// Asked again every five minutes while the facility is on screen, so a call-out reported since appears without a reload. Not every minute, as the
// Messages count is: each refresh re-reads every page shown and recounts the whole window.
const ACTIVITY_REFRESH_MS = 5 * 60_000

const TONE_CLASS: Record<ActivityTone, string> = {
  good: 'bg-green/15 text-green',
  concern: 'bg-coral-lt text-coral',
  neutral: 'bg-marine/15 text-marine',
}

const ENTRY_ICON: Record<Exclude<FacilityActivityEntry['kind'], 'message'>, LucideIcon> = {
  callout: PhoneOff,
  no_show: UserX,
  shift_completed: CircleCheck,
  behavior: ClipboardCheck,
  bridge_opened: Siren,
  staff_message: MessageSquareText,
  bridge_filled: CalendarCheck,
  census_applied: BedDouble,
  schedule_released: Send,
  schedule_board: LayoutList,
}

function EntryIcon({ entry, tone }: { entry: FacilityActivityEntry; tone: ActivityTone }) {
  if (entry.kind === 'message') {
    return <NotificationIcon type={entry.messageType ?? 'score_update'} size="md" />
  }
  const Icon = ENTRY_ICON[entry.kind]
  return (
    <div aria-hidden="true" className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${TONE_CLASS[tone]}`}>
      <Icon strokeWidth={2.25} className="w-4 h-4" />
    </div>
  )
}

/** The picture on each standard behavior's chip; a custom behavior takes the general one. */
const BEHAVIOR_ICON: Record<string, LucideIcon> = {
  call_out_rate: PhoneOff,
  on_time_arrival: Clock,
  charting_completion: ClipboardCheck,
  shift_completion: CircleCheck,
  no_show_rate: UserX,
}
const CUSTOM_BEHAVIOR_ICON: LucideIcon = ClipboardList

const SHIFT_HINT = 'Shift-click to combine with the filters already on'

interface FilterChipProps {
  icon: LucideIcon
  label: string
  count: number
  active: boolean
  /** Told whether Shift was held: that adds this choice to the ones already on rather than replacing them. */
  onClick: (isAdding: boolean) => void
  title?: string
}

/** One choice of what to list: everything, one behavior's events, or the messages. A choice with nothing in the window stays, dimmed - it is still something that could happen here. */
function FilterChip({ icon: Icon, label, count, active, onClick, title }: FilterChipProps) {
  const hasAny = count > 0
  const tone = active ? 'bg-marine-dk text-white border-marine-dk' : hasAny ? 'bg-white text-ink border-marine/30 hover:border-marine' : 'bg-white text-ink-mid/70 border-marine/15 hover:border-marine/40'
  const iconTone = active ? 'border-white/60 text-white' : hasAny ? 'border-marine/40 bg-marine/10 text-marine' : 'border-marine/20 text-ink-mid/60'
  return (
    <button type="button" onClick={(event) => onClick(event.shiftKey)} aria-pressed={active} title={title ?? SHIFT_HINT} className={`inline-flex select-none items-center gap-2 rounded-full border-2 py-1 pl-1 pr-3.5 text-xs font-bold transition-colors ${tone}`}>
      <span aria-hidden="true" className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${iconTone}`}>
        <Icon strokeWidth={2.25} className="h-[18px] w-[18px]" />
      </span>
      {label}
      <span className={`tabular-nums text-sm font-black ${active ? 'text-white' : hasAny ? 'text-marine' : ''}`}>{count}</span>
    </button>
  )
}

interface FacilityActivityCardProps {
  api: FacilityActivityApi
  /** Names for the workers the entries are about; an entry for someone no longer on the roster shows their id. */
  names: ReadonlyMap<string, string>
  /** The facility's units by id, so an entry names a unit rather than its id. */
  units?: ReadonlyMap<string, string>
  /** Opens a worker from an entry. Absent where there is nothing to open (the admin's dialog). */
  onSelectWorker?: (workerId: string) => void
  /** Notes from workers nobody has marked seen, and where to read them: shown as a line under the list. */
  unseenNotes?: { count: number; link: React.ReactNode }
}

/**
 * What is new at a facility: every shift outcome, every custom behavior
 * event and every message its workers received, newest first, from the
 * reader's last sign-in. Each behavior the facility measures is a choice
 * above the list, with its count. The counts always show; the list itself
 * starts closed and opens from the heading or from any chip. Its loading
 * and error states are its own, so it never holds up the screen it sits on.
 */
export function FacilityActivityCard({ api, names, units, onSelectWorker, unseenNotes }: FacilityActivityCardProps) {
  // Null until Show earlier is used: the API then starts from the reader's last sign-in.
  const [since, setSince] = useState<string | null>(null)
  // Which chips are on: none to begin with, so no chip reads as selected until the person picks one. 'All' is a choice like any other, held alone.
  const [choices, setChoices] = useState<readonly string[]>([])
  const narrowed = choices.filter((choice) => choice !== ALL_FILTER)
  // Sorted, so the same chips in any order of clicking are one reading (and one cache entry).
  const filter = narrowed.length === 0 ? null : [...narrowed].sort().join(',')
  // Closed until asked for: the heading says how much there is, and the list is a click away.
  const [isExpanded, setIsExpanded] = useState(false)
  /**
   * A chip is a switch. A plain click makes it the only one on, or - when it already is - takes the filter off again, leaving everything listed and
   * no chip selected. A shift-click adds it to the ones already on, or takes just it away. 'All' never combines: it stands for no filter. Either way
   * the list opens: the counts are always shown, the entries on request.
   */
  const choose = (next: string, isAdding: boolean): void => {
    setChoices((current) => {
      const isOn = current.includes(next)
      if (!isAdding || next === ALL_FILTER) {
        return isOn && current.length === 1 ? [] : [next]
      }
      return isOn ? current.filter((choice) => choice !== next) : [...current.filter((choice) => choice !== ALL_FILTER), next]
    })
    setIsExpanded(true)
  }
  const isOn = (choice: string): boolean => choices.includes(choice)

  const query = useInfiniteQuery({
    queryKey: [...api.queryKey, since, filter],
    queryFn: ({ pageParam }) => api.load({ since, filter, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    placeholderData: keepPreviousData,
    refetchInterval: ACTIVITY_REFRESH_MS,
  })

  const first = query.data?.pages[0]?.activity
  const behaviors = first?.behaviors
  const behaviorsByKey = useMemo(() => new Map((behaviors ?? []).map((behavior): [string, FacilityActivityBehavior] => [behavior.key, behavior])), [behaviors])
  const entries = query.data?.pages.flatMap((page) => page.activity.entries) ?? []
  const messageCount = first?.messageCount ?? 0
  const earlier = first ? earlierSince(first.since) : null

  return (
    <section aria-label="Recent activity">
      <div className="text-[10px] font-bold uppercase tracking-wide text-marine border-b border-marine/15 pb-2 mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span>
          Recent Activity
          {first && <span className="ml-2 normal-case tracking-normal text-ink">{windowLabel(first)}</span>}
          <EndpointHint className="ml-2">{api.endpoint}</EndpointHint>
        </span>
        <span className="flex items-center gap-3">
          {isExpanded && earlier && (
            <button type="button" onClick={() => setSince(earlier.since)} className="normal-case tracking-normal text-[11px] font-bold text-marine hover:text-marine-dk">
              Show the last {earlier.days} days
            </button>
          )}
          {first && (
            <button type="button" onClick={() => setIsExpanded((value) => !value)} aria-expanded={isExpanded} className="normal-case tracking-normal text-[11px] font-bold text-marine hover:text-marine-dk">
              {isExpanded ? 'Hide' : `Show ${first.totalCount ?? entries.length} ${(first.totalCount ?? entries.length) === 1 ? 'entry' : 'entries'}`}
            </button>
          )}
        </span>
      </div>

      {query.isPending ? (
        <Skeleton label="Loading recent activity" />
      ) : isNotFoundError(query.error) ? (
        // The facility exists (the screen around this loaded), so a 404 here is an API deployed before the feed existed.
        <div className="text-xs text-ink-mid">This API does not have recent activity yet; deploy it to switch this on.</div>
      ) : query.isError || !first ? (
        <LoadFailure size="xs" message="Could not load recent activity." onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Show">
            <FilterChip icon={Activity} label="All" count={first?.totalCount ?? 0} active={isOn(ALL_FILTER)} onClick={(isAdding) => choose(ALL_FILTER, isAdding)} />
            {(behaviors ?? []).map((behavior) => (
              <FilterChip
                key={behavior.key}
                icon={BEHAVIOR_ICON[behavior.key] ?? CUSTOM_BEHAVIOR_ICON}
                label={behavior.label}
                count={behavior.count}
                active={isOn(behavior.key)}
                onClick={(isAdding) => choose(behavior.key, isAdding)}
                title={behavior.enabled ? undefined : `Switched off at this facility: recorded, but not counted toward a score. ${SHIFT_HINT}`}
              />
            ))}
            <FilterChip icon={MessageSquare} label="Messages to workers" count={messageCount} active={isOn(MESSAGES_FILTER)} onClick={(isAdding) => choose(MESSAGES_FILTER, isAdding)} />
            <FilterChip icon={LayoutList} label="What the engine did" count={first?.engineCount ?? 0} active={isOn(ENGINE_FILTER)} onClick={(isAdding) => choose(ENGINE_FILTER, isAdding)} />
          </div>

          {!isExpanded ? null : entries.length === 0 ? (
            <div className="border border-marine/15 bg-white px-4 py-4 text-xs text-ink-mid">
              {filter === null ? (first.basis === 'last_sign_in' ? 'Nothing new since you last signed in.' : 'Nothing has been recorded in this time.') : 'Nothing of this kind in this time.'}
            </div>
          ) : (
            <ul className={`border border-marine/15 bg-white divide-y divide-marine/10 ${query.isPlaceholderData ? 'opacity-60' : ''}`}>
              {entries.map((entry) => {
                const line = describeEntry(entry, behaviorsByKey, units)
                const name = names.get(entry.workerId) ?? entry.workerId
                return (
                  <li key={entry.entryId} className="flex items-start gap-3 px-4 py-2.5">
                    <EntryIcon entry={entry} tone={line.tone} />
                    <div className="min-w-0 flex-1 text-xs text-ink">
                      <div>
                        {onSelectWorker ? (
                          <button type="button" onClick={() => onSelectWorker(entry.workerId)} className="font-bold text-marine-dk hover:underline">
                            {name}
                          </button>
                        ) : (
                          <span className="font-bold">{name}</span>
                        )}{' '}
                        {line.text}
                      </div>
                      {line.detail && <div className="text-ink-mid mt-0.5 break-words">{line.detail}</div>}
                    </div>
                    <time dateTime={entry.at} className="shrink-0 text-[11px] text-ink-mid whitespace-nowrap">
                      {formatWhen(entry.at)}
                    </time>
                  </li>
                )
              })}
            </ul>
          )}

          {isExpanded && query.hasNextPage && (
            <div>
              <button type="button" onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage} className="text-[11px] font-bold text-marine hover:text-marine-dk disabled:opacity-60">
                {query.isFetchingNextPage ? 'Loading…' : 'Show more'}
              </button>
            </div>
          )}

          {unseenNotes && unseenNotes.count > 0 && (
            <div className="text-xs text-ink">
              <span className="font-bold text-coral">
                {unseenNotes.count} note{unseenNotes.count === 1 ? '' : 's'} from workers not yet seen.
              </span>{' '}
              {unseenNotes.link}
            </div>
          )}
        </div>
      )}
    </section>
  )
}

